import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialogModule, MatDialogRef, MatDialog } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { take } from 'rxjs';
import { SettingsApiService } from '../../services/api/settings.api.service';
import { KsefTokenDialogComponent } from '../ksef-token-dialog/ksef-token-dialog.component';

interface OnboardingSlide {
  icon: string;
  title: string;
  description: string;
  bullets?: string[];
}

@Component({
  selector: 'app-welcome-onboarding-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule
  ],
  templateUrl: './welcome-onboarding-dialog.component.html',
  styleUrls: ['./welcome-onboarding-dialog.component.scss']
})
export class WelcomeOnboardingDialogComponent {
  private readonly _settingsApiService = inject(SettingsApiService);
  private readonly _dialog = inject(MatDialog);

  currentSlide = 0;

  slides: OnboardingSlide[] = [
    {
      icon: 'tabler_outline:confetti',
      title: 'Witaj w Fakturnia!',
      description: 'Twoje nowe narzędzie do wystawiania faktur — szybko, wygodnie i zgodnie z przepisami.',
      bullets: [
        'Wystawiaj faktury w kilka sekund',
        'Zarządzaj klientami i produktami',
        'Integracja z Krajowym Systemem e-Faktur (KSeF)'
      ]
    },
    {
      icon: 'tabler_outline:file-invoice',
      title: 'Faktury',
      description: 'Twórz, edytuj i zarządzaj dokumentami sprzedaży w jednym miejscu.',
      bullets: [
        'Faktury VAT, proforma, korygujące i zaliczkowe',
        'Automatyczna numeracja dokumentów',
        'Eksport do PDF i wysyłka e-mailem'
      ]
    },
    {
      icon: 'tabler_outline:users',
      title: 'Klienci i produkty',
      description: 'Zbuduj bazę kontrahentów i produktów, by przyspieszyć wystawianie faktur.',
      bullets: [
        'Automatyczne pobieranie danych z GUS po NIP',
        'Katalog produktów z cenami i stawkami VAT',
        'Szybkie wyszukiwanie i autouzupełnianie'
      ]
    },
    {
      icon: 'tabler_outline:settings',
      title: 'Dostosuj aplikację',
      description: 'Skonfiguruj Fakturnia pod swoje potrzeby w ustawieniach.',
      bullets: [
        'Dane sprzedawcy i konto bankowe',
        'Format numeracji i symbole dokumentów',
        'Stawki VAT, podpisy, miejsce wystawienia'
      ]
    },
    {
      icon: 'tabler_outline:file-certificate',
      title: 'Krajowy System e-Faktur',
      description: 'Połącz się z KSeF, by automatycznie wysyłać i odbierać e-faktury zgodnie z wymogami prawa.',
      bullets: [
        'Wystarczy certyfikat KSeF i kilka kliknięć',
        'Automatyczne wysyłanie faktur do KSeF',
        'Pobieranie i weryfikacja statusów e-faktur'
      ]
    }
  ];

  constructor(public dialogRef: MatDialogRef<WelcomeOnboardingDialogComponent>) {}

  get isFirstSlide(): boolean {
    return this.currentSlide === 0;
  }

  get isLastSlide(): boolean {
    return this.currentSlide === this.slides.length - 1;
  }

  next(): void {
    if (!this.isLastSlide) {
      this.currentSlide++;
    }
  }

  prev(): void {
    if (!this.isFirstSlide) {
      this.currentSlide--;
    }
  }

  skip(): void {
    this._markSeen();
    this.dialogRef.close();
  }

  finish(): void {
    this._markSeen();
    this.dialogRef.close();
  }

  connectKsef(): void {
    this._markSeen();
    this.dialogRef.close();
    this._dialog.open(KsefTokenDialogComponent, {
      width: '700px',
      maxWidth: '95vw',
      panelClass: 'fakturnia-responsive-dialog'
    });
  }

  private _markSeen(): void {
    this._settingsApiService.markOnboardingSeen()
      .pipe(take(1))
      .subscribe();
  }
}
